import { businessToday, parseISODate, toISODate } from '../../lib/datetime'
import type { AgendaListItem } from './types'

/*
 * Haftalık takvimin saf tarih hesapları. Günler yerel gece yarısı `Date`'leridir, "bugün" kurumsal
 * gündür (`businessToday`). Gün eklemek `new Date(y, m, d + n)` ile — milisaniye toplamı yaz saati
 * geçişinde günü kaydırırdı. Hafta Pazartesi başlar.
 */

export const WEEK_LENGTH = 7

/** URL'de görüntülenen haftayı taşıyan parametre (`?week=YYYY-MM-DD`, haftanın Pazartesi'si). */
export const WEEK_PARAM = 'week'

/** Gün adı anahtarları — haftadaki sıraya göre (Pazartesi = 0). */
export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

/** Ay adı anahtarları — `Date.getMonth()` sırasına göre. */
export const MONTH_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
}

/** Günün haftadaki sırası: Pazartesi 0 … Pazar 6 (`getDay()` Pazar'ı 0 sayar). */
export function weekdayIndex(date: Date): number {
  return (date.getDay() + 6) % 7
}

/** Verilen günün haftasının Pazartesi'si. */
function mondayOf(date: Date): Date {
  return addDays(date, -weekdayIndex(date))
}

/** URL parametresi → haftanın Pazartesi'si; boş/bozuk değer kurumsal bugünün haftasına düşer. */
export function resolveWeekStart(param: string | null): Date {
  const parsed = param && /^\d{4}-\d{2}-\d{2}$/.test(param) ? parseISODate(param) : null
  return mondayOf(parsed ?? businessToday())
}

/** Pazartesi'den başlayan yedi gün. */
export function weekDays(monday: Date): Date[] {
  return Array.from({ length: WEEK_LENGTH }, (_, i) => addDays(monday, i))
}

/**
 * Hafta başlığı: "12 – 18 Ekim 2026", "28 Eylül – 4 Ekim 2026", "29 Aralık 2025 – 4 Ocak 2026".
 * Ay adı i18n'den gelir — `Intl` çıktısı ICU sürümüne göre değişir (THY test ortamı farklı olabilir).
 */
export function weekTitle(monday: Date, monthName: (month: number) => string): string {
  const sunday = addDays(monday, WEEK_LENGTH - 1)
  const end = `${sunday.getDate()} ${monthName(sunday.getMonth())} ${sunday.getFullYear()}`
  if (monday.getFullYear() !== sunday.getFullYear()) {
    return `${monday.getDate()} ${monthName(monday.getMonth())} ${monday.getFullYear()} – ${end}`
  }
  if (monday.getMonth() !== sunday.getMonth()) {
    return `${monday.getDate()} ${monthName(monday.getMonth())} – ${end}`
  }
  return `${monday.getDate()} – ${end}`
}

/** Bir günün takvim satırı. */
export interface DayEvent {
  item: AgendaListItem
  /** Toplantı bu günden ÖNCE başladı — çok günlü toplantının devam günü (çipte saat yerine "…"). */
  continued: boolean
}

/** Bölgesiz duvar saati → dakika hassasiyetinde karşılaştırma anahtarı (`YYYY-MM-DDTHH:mm`). */
function minuteKey(value: string): string {
  return value.slice(0, 16)
}

function compareText(a: string, b: string): number {
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

/**
 * `day` ile kesişen toplantılar (sunucunun aralık kuralı), başlangıca göre artan; çok günlü toplantı
 * kapsadığı her günde görünür. Sıfır dolgulu değerlerde metin karşılaştırması kronolojiktir.
 */
export function eventsOn(items: readonly AgendaListItem[], day: Date): DayEvent[] {
  const dayStart = `${toISODate(day)}T00:00`
  const nextDayStart = `${toISODate(addDays(day, 1))}T00:00`
  return items
    .filter((it) => minuteKey(it.startTime) < nextDayStart && minuteKey(it.endTime) > dayStart)
    .map((item) => ({ item, continued: minuteKey(item.startTime) < dayStart }))
    .sort(
      (a, b) =>
        compareText(minuteKey(a.item.startTime), minuteKey(b.item.startTime)) ||
        a.item.id - b.item.id,
    )
}

/** Duvar saatinin saat kısmı (`HH:mm`). */
export function timeOf(value: string): string {
  return value.slice(11, 16)
}

/** Bir günün dakika sayısı — saat ızgarasının dikey ekseni. */
export const MINUTES_IN_DAY = 24 * 60

/** Izgarada en az görsel süre (dk): kısa toplantı da tıklanabilsin; çakışma da bu boyla hesaplanır. */
export const MIN_VISUAL_MINUTES = 20

/** Duvar saatinin gün içi dakikası (`HH:mm` → 0…1439). */
function minuteOfDay(value: string): number {
  return Number(value.slice(11, 13)) * 60 + Number(value.slice(14, 16))
}

/** Saat ızgarasına yerleştirilmiş toplantı: dikey aralık (dakika) + çakışma grubundaki sütunu. */
export interface PositionedEvent extends DayEvent {
  /** Gün içinde başlangıç dakikası — önceki günden taşan toplantıda 0. */
  top: number
  /** Kapladığı dakika (en az {@link MIN_VISUAL_MINUTES}, gün sonunu aşmaz). */
  span: number
  /** Çakışma grubundaki sütun sırası (0'dan). */
  column: number
  /** Çakışma grubunun sütun sayısı — genişlik `1 / columns`. */
  columns: number
}

/**
 * Günün toplantılarını saat ızgarasına yerleştirir: kesişenler bir çakışma grubunda yan yana
 * sütunlara (boş ilk sütuna) dağılır; ertesi güne taşan gün sonunda kesilir. `events` sıralı
 * {@link eventsOn} çıktısıdır.
 */
export function layoutDay(events: readonly DayEvent[], day: Date): PositionedEvent[] {
  const nextDayStart = `${toISODate(addDays(day, 1))}T00:00`
  const placed: PositionedEvent[] = []
  let group: PositionedEvent[] = []
  let columnEnds: number[] = []
  let groupEnd = -1

  const closeGroup = () => {
    for (const ev of group) ev.columns = columnEnds.length
    group = []
    columnEnds = []
  }

  for (const ev of events) {
    const top = ev.continued ? 0 : minuteOfDay(ev.item.startTime)
    const end =
      minuteKey(ev.item.endTime) >= nextDayStart ? MINUTES_IN_DAY : minuteOfDay(ev.item.endTime)
    const visualEnd = Math.min(MINUTES_IN_DAY, Math.max(end, top + MIN_VISUAL_MINUTES))
    if (top >= groupEnd) closeGroup()
    let column = columnEnds.findIndex((colEnd) => colEnd <= top)
    if (column === -1) column = columnEnds.push(visualEnd) - 1
    else columnEnds[column] = visualEnd
    groupEnd = Math.max(groupEnd, visualEnd)
    const positioned = { ...ev, top, span: visualEnd - top, column, columns: 1 }
    group.push(positioned)
    placed.push(positioned)
  }
  closeGroup()
  return placed
}

/** Europe/Istanbul gün içi dakikası ("şimdi" çizgisi); dilim tanınmazsa yerel saate düşer. */
export function businessMinuteNow(now: Date = new Date()): number {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Istanbul',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now)
    const part = (type: string) => Number(parts.find((p) => p.type === type)?.value)
    const minutes = part('hour') * 60 + part('minute')
    if (Number.isFinite(minutes)) return minutes
  } catch {
    // Dilim tanınmadı — yerel saate düş.
  }
  return now.getHours() * 60 + now.getMinutes()
}

/** "Yeni Davet"in önerdiği gün: hafta ileride ise Pazartesi'si, değilse bugün (geçmişe davet olmaz). */
export function suggestedDay(monday: Date, today: Date): Date {
  return monday > today ? monday : today
}
